import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { FeeCategory } from '@/apis/extended/admin/interestRateApi';
import type { ProductEsitmation } from '@/types/ProductType';

export type CalculatedFeeKey =
  | 'appraisalFeeBase'
  | 'creditReportFeeBase'
  | 'processingFeeBase'
  | 'underwritingFee'
  | 'applicationFeeBase'
  | 'floodMonitoringFee'
  | 'floodDeterminationFee'
  | 'rentalIncomeReportFee'
  | 'commitmentFee'
  | 'recordingFees'
  | 'transferTaxes'
  | 'titleSettlementAgentFee'
  | 'titleLendersTitleInsuranceBase'
  | 'optionalOwnersTitleInsurance'
  | 'surveyFee'
  | 'upfrontHomeownersInsurance'
  | 'prepaidsPropertyTaxes'
  | 'prepaidsHomeownersInsurance'
  | 'prepaidInterest'
  | 'upfrontMIPfee'
  | 'upfrontGuaranteeFee'
  | 'vaFundingFee';

export type CalculatedFeeCatalogItem = {
  key: CalculatedFeeKey;
  label: string;
  defaultDescription: string;
  category: FeeCategory;
  // compute amount from estimation
  compute: (est: ProductEsitmation) => number;
  // default visibility/APR inclusion when no admin rule exists
  defaultVisible: boolean;
  defaultIncludedInApr: boolean;
  // if false, omit item entirely for loan types that don't support it
  isApplicable?: (est: ProductEsitmation) => boolean;
};

const safeNum = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const CALCULATED_FEE_CATALOG: CalculatedFeeCatalogItem[] = [
  {
    key: 'appraisalFeeBase',
    label: 'Appraisal Fee',
    defaultDescription:
      'Cost of the property appraisal required by the lender.',
    category: 'lender',
    compute: (e) => safeNum((e as any).appraisalFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'creditReportFeeBase',
    label: 'Credit Report',
    defaultDescription: 'Fee to pull and review borrower credit reports.',
    category: 'lender',
    compute: (e) => safeNum((e as any).creditReportFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'processingFeeBase',
    label: 'Processing Fee',
    defaultDescription: 'Lender processing and file preparation fee.',
    category: 'lender',
    compute: (e) => safeNum((e as any).processingFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'underwritingFee',
    label: 'Underwriting Fee',
    defaultDescription: 'Fee for underwriting review and loan approval.',
    category: 'lender',
    compute: (e) => safeNum((e as any).underwritingFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'applicationFeeBase',
    label: 'Application Fee',
    defaultDescription: 'Fee charged when the loan application is submitted.',
    category: 'lender',
    compute: (e) => safeNum((e as any).applicationFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'floodMonitoringFee',
    label: 'Flood Monitoring',
    defaultDescription: 'Monitoring service for flood zone status changes.',
    category: 'lender',
    compute: (e) => safeNum((e as any).floodMonitoringFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'floodDeterminationFee',
    label: 'Flood Determination',
    defaultDescription: 'Determines whether the property is in a flood zone.',
    category: 'lender',
    compute: (e) => safeNum((e as any).floodDeterminationFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'rentalIncomeReportFee',
    label: 'Rental Income Report',
    defaultDescription: 'Report used to verify rental income for investors.',
    category: 'lender',
    compute: (e) => safeNum((e as any).rentalIncomeReportFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
    isApplicable: (e) =>
      (e as any).type === USER_LOAN_PROGRAM.DSCR ||
      (e as any).type === USER_LOAN_PROGRAM.COMMERCIAL,
  },
  {
    key: 'commitmentFee',
    label: 'Commitment Fee',
    defaultDescription: 'Fee to reserve lender capital for the loan.',
    category: 'lender',
    compute: (e) => safeNum((e as any).commitmentFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.COMMERCIAL,
  },
  {
    key: 'recordingFees',
    label: 'Recording Fees',
    defaultDescription:
      'County and state recording charges for loan documents.',
    category: 'government',
    compute: (e) =>
      safeNum((e as any).countyRecordingFees) +
      safeNum((e as any).stateRecordingFees) +
      safeNum((e as any).recordingTitle) +
      safeNum((e as any).recordingMortgage) +
      safeNum((e as any).recordingDeedBase),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'transferTaxes',
    label: 'Transfer Taxes',
    defaultDescription: 'Government taxes due on property transfer.',
    category: 'government',
    compute: (e) => safeNum((e as any).transferTaxes),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'titleSettlementAgentFee',
    label: 'Title & Settlement',
    defaultDescription: 'Title company and settlement services fee.',
    category: 'title',
    compute: (e) => safeNum((e as any).titleSettlementAgentFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'titleLendersTitleInsuranceBase',
    label: "Lender's Title Insurance",
    defaultDescription: 'Title insurance policy protecting the lender.',
    category: 'title',
    compute: (e) => safeNum((e as any).titleLendersTitleInsuranceBase),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'optionalOwnersTitleInsurance',
    label: "(Optional) Owner's Title Insurance",
    defaultDescription: 'Optional title insurance policy for the buyer.',
    category: 'title',
    compute: (e) => safeNum((e as any).optionalOwnersTitleInsurance),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'surveyFee',
    label: 'Survey Fee',
    defaultDescription: 'Fee for property survey or boundary verification.',
    category: 'title',
    compute: (e) => safeNum((e as any).surveyFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'upfrontHomeownersInsurance',
    label: 'Homeowners Policy',
    defaultDescription: 'Initial premium for homeowners insurance.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).upfrontHomeownersInsurance),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'prepaidsPropertyTaxes',
    label: 'Prepaid Taxes (Escrow)',
    defaultDescription: 'Prepaid property taxes collected for escrow.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).prepaidsPropertyTaxes),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'prepaidsHomeownersInsurance',
    label: 'Prepaid Insurance (Escrow)',
    defaultDescription: 'Prepaid homeowners insurance for escrow.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).prepaidsHomeownersInsurance),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'prepaidInterest',
    label: 'Prepaid Interest',
    defaultDescription: 'Interest due from closing date to first payment.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).prepaidInterest),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'upfrontMIPfee',
    label: 'Upfront MIP Fee',
    defaultDescription: 'Upfront mortgage insurance premium for FHA loans.',
    category: 'government',
    compute: (e) => safeNum((e as any).upfrontMIPfee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.FHA,
  },
  {
    key: 'upfrontGuaranteeFee',
    label: 'Upfront Guarantee Fee',
    defaultDescription: 'Upfront guarantee fee for USDA loans.',
    category: 'government',
    compute: (e) => safeNum((e as any).upfrontGuaranteeFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.USDA,
  },
  {
    key: 'vaFundingFee',
    label: 'VA Funding Fee',
    defaultDescription: 'Funding fee charged on VA loans.',
    category: 'government',
    compute: (e) => safeNum((e as any).vaFundingFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.VA,
  },
];
